import {
	DeviceAuthenticationStatus,
	mapDeviceAuthenticationFromSysApi
} from '@shure/shared/angular/data-access/device-authentication';
import {
	AudioChannelGroup,
	AudioChannelRole,
	AutomixMode,
	DistanceMeasurement
} from '@shure/shared/angular/data-access/system-api/core';
import {
	mapCoverageModeFromSysApi,
	mapDeviceModelFromSysApi,
	mapFragmentFeaturesToModel,
	mapMeasurementUnitFromSysApi,
	mapOutputModeFromSysApi,
	mapPlacementFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import { PositionUnit } from '@shure/shared/angular/utils/utils-position';

import { CoverageDevice, CoverageTalkerPosition, PositionUnitRange } from '../../api/models';
import { DeviceCoverageDeviceFragment } from '../graphql/generated/device-coverage-device.generated';

import { mapAnalogOutputChannelFromSysApi } from './coverage-analog-audio-channel.mapper';
import { mapCoverageAudioChannelFromSysApi } from './coverage-audio-channel.mapper';
import { mapCoverageAudioCoverageAreaFromSysApi } from './coverage-audio-coverage-area.mapper';

export function mapCoverageDeviceFromSysApi(
	fragment: DeviceCoverageDeviceFragment,
	authentication: DeviceAuthenticationStatus,
	usePostGateValues: boolean
): CoverageDevice {
	const deviceHeight = fragment.features.height?.height ?? 0;
	const id = fragment.id;
	const model = mapDeviceModelFromSysApi(fragment.description.model);
	const virtual = fragment.virtual;

	const automixmodeManualChannelId = fragment.features.audioChannels?.audioChannels.find(
		(c) => c.description.role === AudioChannelRole.Filter && c.features.automixer?.mode === AutomixMode.Manual
	)?.id;

	return {
		id,
		model,
		status: fragment.status,
		virtual,
		automixmodeManualChannelId,
		constraints: {
			talkerTrackingConfig: {
				positionUpdateRate: fragment.description.constraints.talkerTrackingConfig?.positionUpdateRate.range ?? {
					min: 100,
					max: 99999,
					step: 1
				}
			},
			height: fragment.description.constraints.height
				? {
						height: fragment.description.constraints.height.height.range ?? { min: 0, max: 0, step: 1 }
					}
				: undefined,
			name: fragment.description.constraints.name?.name.pattern ?? undefined
		},
		features: mapFragmentFeaturesToModel(fragment, {
			audioChannels: () => ({
				value: (fragment.features.audioChannels?.audioChannels ?? [])
					.filter((c) => c.description.role === AudioChannelRole.Source)
					.map((c) =>
						mapCoverageAudioChannelFromSysApi(c, { id, model, virtual, deviceHeight, usePostGateValues })
					)
			}),
			analogOutputChannels: {
				source: 'audioChannels',
				map: () => ({
					value: (fragment.features.audioChannels?.audioChannels ?? [])
						.filter((c) =>
							[AudioChannelGroup.AnalogOutput, AudioChannelGroup.Analog_70VOutput].includes(
								c.description.group
							)
						)
						.map((c) => mapAnalogOutputChannelFromSysApi(c))
				})
			},
			audioCoverageAreas: () => ({
				value: (fragment.features.audioCoverageAreas?.audioCoverageAreas ?? []).map((c) =>
					mapCoverageAudioCoverageAreaFromSysApi(c)
				)
			}),
			analogAudioOutputMode: () => ({
				value: mapOutputModeFromSysApi(fragment.features.analogAudioOutputMode?.mode)
			}),
			authentication: () => ({
				value: mapDeviceAuthenticationFromSysApi(
					fragment.features.authentication,
					authentication.authenticated,
					authentication.readonlyMode
				)
			}),
			allEqualizersBypass: () => ({
				value: fragment.features.allEqualizersBypass?.bypassed ?? false
			}),

			coverageMode: () => ({ value: mapCoverageModeFromSysApi(fragment.features.coverageMode?.coverageMode) }),
			position: {
				source: 'coveragePosition',
				map: () => ({
					value: {
						x: PositionUnit.fromRaw(fragment.features.coveragePosition?.xPosition ?? 0),
						y: PositionUnit.fromRaw(fragment.features.coveragePosition?.yPosition ?? 0)
					}
				})
			},
			height: () => ({
				value: PositionUnit.fromRaw(fragment.features.height?.height ?? 0)
			}),
			listenerHeight: () => ({
				value: PositionUnit.fromRaw(fragment.features.listenerHeight?.height ?? 0)
			}),
			rotation: () => ({
				value: {
					xAxis: fragment.features.rotation?.xAxis ?? 0,
					yAxis: fragment.features.rotation?.yAxis,
					zAxis: fragment.features.rotation?.zAxis ?? 0
				}
			}),
			placement: () => ({ value: mapPlacementFromSysApi(fragment.features.placement?.placement) }),
			detectedPlacement: () => ({
				value: mapPlacementFromSysApi(fragment.features.detectedPlacement?.detectedPlacement)
			}),
			audioAutoFocus: () => ({ value: { enabled: fragment.features.audioAutoFocus?.enabled ?? false } }),
			debugMode: () => ({ value: { enabled: fragment.features.debugMode?.enabled ?? false } }),
			dspDebugMode: () => ({ value: { enabled: fragment.features.dspDebugMode?.enabled ?? false } }),
			identify: () => ({ value: { identifying: !!fragment.features.identify?.identifying } }),
			talkerTrackingConfig: () => ({
				value: {
					localizationSensitivity: fragment.features.talkerTrackingConfig?.localizationSensitivity,
					metricsIncluded: fragment.features.talkerTrackingConfig?.metricsIncluded,
					positionUpdateRate: fragment.features.talkerTrackingConfig?.positionUpdateRate,
					reflectionCorrection: fragment.features.talkerTrackingConfig?.reflectionCorrection,
					voiceActivityDetectionSensitivity:
						fragment.features.talkerTrackingConfig?.voiceActivityDetectionSensitivity
				}
			}),
			name: () => ({ value: fragment.features.name?.name ?? '' }),
			coverageLock: () => ({ value: { enabled: fragment.features.coverageLock?.enabled ?? false } }),
			automixGainMetering: () => ({
				value: { enabled: fragment.features.automixGainMetering?.enabled ?? false }
			}),
			mute: {
				source: 'audioMute',
				map: () => ({ value: { muted: fragment.features.audioMute?.muted ?? false } })
			},
			danteName: {
				source: 'danteAudioNetwork',
				map: () => ({ value: fragment.features.danteAudioNetwork?.name ?? '' })
			},
			userPerspective: () => ({
				value: {
					deviceRotation: {
						xAxis: fragment.features.userPerspective?.deviceRotation.xAxis ?? 0,
						yAxis: fragment.features.userPerspective?.deviceRotation.yAxis ?? 0,
						zAxis: fragment.features.userPerspective?.deviceRotation.zAxis ?? 0
					},
					viewRotation: {
						angle: fragment.features.userPerspective?.viewRotation.angle ?? 0
					}
				}
			}),
			measurementUnit: {
				source: 'userInterfacePreferences',
				map: () => ({
					value: mapMeasurementUnitFromSysApi(
						fragment.features.userInterfacePreferences?.unitOfMeasurement?.distance ??
							DistanceMeasurement.Feet
					)
				})
			}
		})
	};
}

export function mapTalkerPositionFromSysApi(
	confidenceLevel: number,
	xRaw: number,
	yRaw: number,
	zRaw: number
): CoverageTalkerPosition {
	return {
		confidenceLevel,
		x: PositionUnit.fromRaw(xRaw),
		y: PositionUnit.fromRaw(yRaw),
		z: PositionUnit.fromRaw(zRaw)
	};
}

export function mapToPositionUnitRange(range?: { min?: number; max?: number; step?: number }): PositionUnitRange {
	return {
		min: PositionUnit.fromRaw(range?.min ?? 0),
		max: PositionUnit.fromRaw(range?.max ?? 0),
		step: PositionUnit.fromRaw(range?.step ?? 0)
	};
}
